import React, { useEffect, useMemo, useState } from "react";
import {
  EuiBasicTable,
  EuiBasicTableColumn,
  EuiButton,
  EuiCallOut,
  EuiEmptyPrompt,
  EuiSpacer,
  EuiSwitch,
  EuiText,
} from "@elastic/eui";
import { AdminPermissionsResponse, useApi } from "../";
import { useAnketaAuth } from "../auth/context";
import { useAnketaI18n } from "src/internationalization";
import { useToast } from "src/notifications";
import { TableSkeleton } from "src/components/skeleton";

type Matrix = Record<string, Record<string, Record<string, boolean>>>;

interface Row {
  resource: string;
  action: string;
}

/**
 * Editor for the runtime ABAC matrix (superuser-only). Rows are
 * `resource · action`, columns are roles; each cell toggles whether that role is
 * allowed. Saving PUTs the whole matrix to /api/v2/admin/permissions. The backend
 * remains the source of truth and falls back to the hardcoded ROLES defaults.
 */
export const AdminPermissionsEditor = (): JSX.Element => {
  const { Get, Put } = useApi();
  const { keycloak } = useAnketaAuth();
  const { translate } = useAnketaI18n();
  const { addToast } = useToast();

  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [forbidden, setForbidden] = useState<boolean>(false);

  const [roles, setRoles] = useState<string[]>([]);
  const [resourceActions, setResourceActions] = useState<
    Record<string, string[]>
  >({});
  const [draft, setDraft] = useState<Matrix>({});

  const load = async () => {
    setLoading(true);
    setError(null);
    setForbidden(false);
    try {
      const resp = await Get<any>({
        path: "v2/admin/permissions",
        responseType: "json",
        token: keycloak?.token,
      });
      if (!resp.success && (resp.status === 403 || resp.status === 401)) {
        setForbidden(true);
        return;
      }
      const payload: AdminPermissionsResponse | null =
        (resp?.data as any)?.data ?? null;
      if (!payload) {
        setError(
          translate("anketa.admin.permissions.load.error", {
            defaultMessage: "Permissions could not be loaded.",
          }),
        );
        return;
      }
      setRoles(payload.roles);
      setResourceActions(payload.resourceActions ?? {});
      setDraft(JSON.parse(JSON.stringify(payload.matrix ?? {})) as Matrix);
    } catch (e) {
      setError(
        translate("anketa.admin.permissions.load.error", {
          defaultMessage: "Permissions could not be loaded.",
        }),
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (keycloak?.token) {
      load();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keycloak?.token]);

  const isAllowed = (role: string, resource: string, action: string): boolean =>
    draft[role]?.[resource]?.[action] === true;

  const toggle = (role: string, resource: string, action: string) => {
    setDraft((prev) => {
      const next: Matrix = JSON.parse(JSON.stringify(prev));
      next[role] ??= {};
      next[role][resource] ??= {};
      next[role][resource][action] = !next[role]?.[resource]?.[action];
      return next;
    });
  };

  const rows = useMemo<Row[]>(() => {
    const out: Row[] = [];
    for (const [resource, actions] of Object.entries(resourceActions)) {
      for (const action of actions) {
        out.push({ resource, action });
      }
    }
    return out;
  }, [resourceActions]);

  const columns = useMemo<EuiBasicTableColumn<Row>[]>(() => {
    const cols: EuiBasicTableColumn<Row>[] = [
      {
        name: translate("anketa.admin.permissions.column.resource_action", {
          defaultMessage: "Resource · Action",
        }),
        render: (row: Row) => (
          <EuiText size="s">
            <strong>{row.resource}</strong> · {row.action}
          </EuiText>
        ),
      },
    ];
    for (const role of roles) {
      cols.push({
        name: role,
        align: "center",
        render: (row: Row) => (
          <EuiSwitch
            label=""
            showLabel={false}
            compressed
            aria-label={translate("anketa.admin.permissions.toggle.aria", {
              defaultMessage:
                "{{role}}: allow {{action}} on {{resource}}",
              values: { role, action: row.action, resource: row.resource },
            })}
            checked={isAllowed(role, row.resource, row.action)}
            onChange={() => toggle(role, row.resource, row.action)}
          />
        ),
      });
    }
    return cols;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roles, draft]);

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const resp = await Put<any>({
        path: "v2/admin/permissions",
        responseType: "json",
        token: keycloak?.token,
        value: { matrix: draft },
      });
      if (resp?.success) {
        // DESIGN.md: mutations report via toast, not an inline callout.
        addToast({
          color: "success",
          iconType: "checkInCircleFilled",
          title: translate("anketa.admin.permissions.saved", {
            defaultMessage: "Permissions saved.",
          }),
        });
        await load();
      } else {
        addToast({
          color: "danger",
          iconType: "alert",
          title: translate("anketa.admin.save.error", {
            defaultMessage: "Saving failed.",
          }),
          text: resp?.error?.message,
        });
      }
    } catch (e) {
      addToast({
        color: "danger",
        iconType: "alert",
        title: translate("anketa.admin.save.error", {
          defaultMessage: "Saving failed.",
        }),
        text: e instanceof Error ? e.message : undefined,
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <TableSkeleton rows={8} />;
  }

  if (forbidden) {
    return (
      <EuiEmptyPrompt
        iconType="lock"
        color="warning"
        title={
          <h2>
            {translate("anketa.admin.forbidden.title", {
              defaultMessage: "Access denied",
            })}
          </h2>
        }
        body={
          <p>
            {translate("anketa.admin.forbidden.body", {
              defaultMessage:
                "You do not have permission to view this section.",
            })}
          </p>
        }
      />
    );
  }

  return (
    <>
      <EuiText size="s" color="subdued">
        {translate("anketa.admin.permissions.description", {
          defaultMessage:
            "Controls which role may perform which action. The server is the authoritative instance; when the table is empty the stored default values apply.",
        })}
      </EuiText>
      <EuiSpacer size="m" />
      {error && (
        <>
          <EuiCallOut title={error} color="danger" iconType="alert">
            <EuiButton
              color="danger"
              size="s"
              iconType="refresh"
              onClick={() => load()}
            >
              {translate("anketa.common.retry", { defaultMessage: "Retry" })}
            </EuiButton>
          </EuiCallOut>
          <EuiSpacer size="m" />
        </>
      )}
      <EuiBasicTable<Row> items={rows} columns={columns} tableLayout="auto" />
      <EuiSpacer size="m" />
      <EuiButton fill onClick={save} isLoading={saving}>
        {translate("anketa.admin.save.button", {
          defaultMessage: "Save",
        })}
      </EuiButton>{" "}
      <EuiButton onClick={load} isDisabled={saving}>
        {translate("anketa.admin.reset.button", {
          defaultMessage: "Reset",
        })}
      </EuiButton>
    </>
  );
};
